import { AnimatePresence, motion } from 'framer-motion';
import React, { useCallback, useState } from 'react';
import { designApi } from '../services/designApi';

const EnhancedDesignUpload = ({
  product,
  onDesignSelect,
  designData,
  className = ""
}) => {
  const [uploadStatus, setUploadStatus] = useState('idle');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploadedDesigns, setUploadedDesigns] = useState(Array.isArray(designData) ? designData : (designData ? [designData] : []));

  const validateFile = (file) => {
    const maxSize = 50 * 1024 * 1024;
    const allowedTypes = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/svg+xml',
      'application/pdf', 'application/postscript', 'image/x-adobe-dng'
    ];

    if (!allowedTypes.includes(file.type)) {
      throw new Error('Invalid file type. Please upload JPEG, PNG, WebP, SVG, PDF, EPS, or DNG files.');
    }

    if (file.size > maxSize) {
      throw new Error('File size too large. Maximum size is 50MB.');
    }

    return true;
  };

  const processDesignResponse = (design, file) => {
    return {
      id: design?._id || design?.id || 'upload-' + Date.now(),
      type: 'upload',
      name: design?.name || file.name,
      url: design?.originalFile?.url || design?.url || URL.createObjectURL(file),
      cloudinaryId: design?.originalFile?.public_id || design?.public_id || 'local-' + Date.now(),
      originalName: file.name,
      size: file.size,
      format: design?.originalFile?.format || file.type.split('/')[1] || 'unknown',
      description: design?.description || `Uploaded ${file.name}`,
      uploadedAt: new Date().toISOString()
    };
  };

  const handleMultipleFileUpload = useCallback(async (files) => {
    try {
      const fileArray = Array.from(files);

      fileArray.forEach(file => {
        validateFile(file);
      });

      setUploadStatus('uploading');
      setUploadError(null);
      setUploadProgress(0);

      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 5;
        });
      }, 200);

      const metadata = {
        category: product?.category || 'business-cards',
        description: `Design upload for ${product?.name || 'product'}`
      };

      console.log(`🔄 Starting multiple design upload for ${fileArray.length} files...`);
      const result = await designApi.uploadMultipleDesigns(fileArray, metadata);
      console.log('✅ Multiple design upload successful:', result);

      clearInterval(progressInterval);
      setUploadProgress(100);

      let designs = [];

      if (result?.data?.designs && Array.isArray(result.data.designs)) {
        designs = result.data.designs.map((design, index) =>
          processDesignResponse(design, fileArray[index])
        );
      } else if (result?.designs && Array.isArray(result.designs)) {
        designs = result.designs.map((design, index) =>
          processDesignResponse(design, fileArray[index])
        );
      } else {
        throw new Error('Unexpected response format from server');
      }

      const newDesigns = [...uploadedDesigns, ...designs];
      setUploadedDesigns(newDesigns);
      setUploadStatus('success');

      if (onDesignSelect) {
        onDesignSelect(newDesigns.length === 1 ? newDesigns[0] : newDesigns);
      }

      setTimeout(() => {
        setUploadProgress(0);
      }, 1000);

    } catch (error) {
      console.error('Upload error:', error);
      setUploadStatus('error');
      setUploadError(error.message);
      setUploadProgress(0);
    }
  }, [product, onDesignSelect, uploadedDesigns]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleMultipleFileUpload(files);
    }
  }, [handleMultipleFileUpload]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleFileInputChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleMultipleFileUpload(files);
    }
    e.target.value = '';
  };

  const removeDesign = (designId) => {
    const updatedDesigns = uploadedDesigns.filter(d => d.id !== designId);
    setUploadedDesigns(updatedDesigns);

    if (onDesignSelect) {
      if (updatedDesigns.length === 0) {
        onDesignSelect(null);
      } else if (updatedDesigns.length === 1) {
        onDesignSelect(updatedDesigns[0]);
      } else {
        onDesignSelect(updatedDesigns);
      }
    }
  };

  const removeAllDesigns = () => {
    setUploadedDesigns([]);
    setUploadStatus('idle');
    setUploadProgress(0);
    setUploadError(null);
    if (onDesignSelect) {
      onDesignSelect(null);
    }
  };

  return (
    <div className={`space-y-3 sm:space-y-4 ${className}`}>
      <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
        <h3 className="text-base sm:text-lg font-bold mb-3 sm:mb-4 flex items-center">
          <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
          </svg>
          Upload Your Design{uploadedDesigns.length > 0 ? ` (${uploadedDesigns.length} uploaded)` : ''}
        </h3>

        <AnimatePresence mode="wait">
          {uploadStatus === 'idle' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <div
                className={`border-2 border-dashed rounded-xl p-4 sm:p-8 text-center transition-all cursor-pointer ${dragActive
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
                  }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => document.getElementById('design-file-input').click()}
              >
                <div className="space-y-2 sm:space-y-4">
                  <div className="flex justify-center">
                    <svg className="w-8 h-8 sm:w-12 sm:h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm sm:text-lg font-semibold text-gray-700">
                      Drop your designs here or click to browse
                    </p>
                    <p className="text-xs sm:text-sm text-gray-500 mt-1 sm:mt-2">
                      Supports: JPEG, PNG, WebP, SVG, PDF, EPS, DNG
                    </p>
                    <p className="text-[10px] sm:text-xs text-gray-400 mt-0.5 sm:mt-1">
                      Maximum file size: 50MB per file • You can select multiple files
                    </p>
                  </div>
                </div>
                <input
                  id="design-file-input"
                  type="file"
                  className="hidden"
                  accept=".jpg,.jpeg,.png,.webp,.svg,.pdf,.eps,.dng"
                  multiple
                  onChange={handleFileInputChange}
                />
              </div>
            </motion.div>
          )}

          {uploadStatus === 'uploading' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="text-center py-4 sm:py-8"
            >
              <div className="flex justify-center mb-3 sm:mb-4">
                <svg className="w-8 h-8 sm:w-12 sm:h-12 text-blue-600 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              <p className="text-sm sm:text-lg font-semibold text-gray-700 mb-1.5 sm:mb-2">Uploading your designs...</p>
              <div className="w-full bg-gray-200 rounded-full h-1.5 sm:h-2 mb-3 sm:mb-4">
                <div
                  className="bg-blue-600 h-1.5 sm:h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
              <p className="text-xs sm:text-sm text-gray-500">{uploadProgress}% complete</p>
            </motion.div>
          )}

          {uploadStatus === 'success' && uploadedDesigns.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-2 sm:space-y-3"
            >
              <div className="bg-green-50 border border-green-200 rounded-xl p-3 sm:p-4">
                <div className="flex items-center justify-between mb-2 sm:mb-3">
                  <div className="flex items-center space-x-1.5 sm:space-x-2">
                    <svg className="w-5 h-5 sm:w-6 sm:h-6 text-[#008000]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    <h4 className="text-sm sm:text-lg font-semibold text-[#008000]">
                      {uploadedDesigns.length} design{uploadedDesigns.length > 1 ? 's' : ''} uploaded successfully!
                    </h4>
                  </div>
                  {uploadedDesigns.length > 0 && (
                    <button
                      onClick={removeAllDesigns}
                      className="text-xs sm:text-sm text-red-600 hover:text-red-800 font-medium"
                    >
                      Remove All
                    </button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                {uploadedDesigns.map((design) => (
                  <motion.div
                    key={design.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-gray-50 border border-gray-200 rounded-lg p-2 sm:p-3 flex items-center space-x-2 sm:space-x-3"
                  >
                    <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-gray-200 rounded overflow-hidden">
                      {design.url && (
                        <img
                          src={design.url}
                          alt={design.originalName}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs sm:text-sm font-medium text-gray-900 truncate">
                        {design.originalName}
                      </p>
                      <p className="text-[10px] sm:text-xs text-gray-500">
                        {(design.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    <button
                      onClick={() => removeDesign(design.id)}
                      className="flex-shrink-0 text-red-600 hover:text-red-800 transition-colors"
                      title="Remove design"
                    >
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                      </svg>
                    </button>
                  </motion.div>
                ))}
              </div>

              {uploadStatus === 'success' && (
                <div className="text-center">
                  <button
                    onClick={() => {
                      setUploadStatus('idle');
                      document.getElementById('design-file-input').click();
                    }}
                    className="text-xs sm:text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Upload More Designs
                  </button>
                </div>
              )}
            </motion.div>
          )}

          {uploadStatus === 'error' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="bg-red-50 border border-red-200 rounded-xl p-4 sm:p-6"
            >
              <div className="flex items-center space-x-2 sm:space-x-4">
                <div className="flex-shrink-0">
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>
                <div>
                  <h4 className="text-sm sm:text-lg font-semibold text-red-800">Upload failed</h4>
                  <p className="text-xs sm:text-sm text-red-600">{uploadError}</p>
                  <button
                    onClick={() => setUploadStatus('idle')}
                    className="text-xs sm:text-sm text-blue-600 hover:text-blue-800 font-medium mt-1 sm:mt-2"
                  >
                    Try again
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="mt-4 sm:mt-6 bg-green-50 border border-green-200 rounded-xl p-3 sm:p-4">
          <h4 className="font-semibold text-xs sm:text-sm text-[#008000] mb-1.5 sm:mb-2">Design Guidelines</h4>
          <ul className="text-xs sm:text-sm text-[#008000] space-y-0.5 sm:space-y-1">
            <li>• For business cards: Minimum 1050×600 pixels (300 DPI)</li>
            <li>• Include 3mm bleed area around your design</li>
            <li>• Keep important text 3mm away from edges</li>
            <li>• Use high-resolution images for best print quality</li>
            <li>• CMYK color mode recommended for accurate colors</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default EnhancedDesignUpload;
